import Quill from 'quill'
import scrollIntoView from 'scroll-into-view-if-needed'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import { addEventBusEvents, sleep } from '@/helpers/utils'
import { lastIndexOfRegex } from './utils'

const Module = Quill.import('core/module')

// Have to escape these chars now with .map((c) => `\\${c}`)
// since we're now building the regex this way
//
export const allowedChars = ['s', '(', '"', "'"].map((c) => `\\${c}`).join('|')

const indexREGEX = RegExp(`((${allowedChars}|^|\\n)@)`, 'g')

const ACTIVE_CLASS = 'mention-active'
export const MIN_CHARS = 4

class Mentions extends Module {
	constructor(quill, options) {
		super(quill, options)

		this.quill = quill
		this.container = document.createElement('div')
		this.container.classList.add('mention-completions')
		this.quill.container.appendChild(this.container)

		// set up elements for loading state
		this.loadingDiv = document.createElement('div')
		this.loadingDiv.className = 'mention-completions-loading'
		this.loadingDiv.style.display = 'none'
		this.container.appendChild(this.loadingDiv)

		this.open = false

		const {
			getExplicitResults,
			getImplicitResults,
			getSuggestion,
			getUsersInText,
			onSuggestionsChange,
		} = options
		Object.assign(this, {
			getExplicitResults,
			getImplicitResults,
			getSuggestion,
			getUsersInText,
			onSuggestionsChange,
			suggestions: [],
		})

		const bindings = [
			{ key: 'ArrowUp', handler: this.moveToPrev.bind(this) }, // ArrowUp
			{ key: 'ArrowDown', handler: this.moveToNext.bind(this) }, // ArrowDown
			{ key: 'Space', handler: () => this.selectUser(true, true) }, // Spacebar
			{ key: '.', handler: () => this.selectUser(true) }, // Period
			{ key: '/', shiftKey: true, handler: () => this.selectUser(true) }, // Question mark
			{ key: '!', shiftKey: true, handler: () => this.selectUser(true) }, // Exclamation point
			{ key: "'", shiftKey: null, handler: () => this.selectUser(true) }, // Single quote
			{ key: '"', shiftKey: true, handler: () => this.selectUser(true) }, // Double quote
		]

		bindings.forEach(({ handler, ...info }) => {
			quill.keyboard.addBinding(info, { collapsed: true }, handler)
		})

		const otherHandlers = [
			{ key: 'Tab', handler: () => this.selectUser() }, // Tab
			{ key: 'Enter', shiftKey: null, handler: () => this.selectUser() }, // Enter
			{ key: 'Enter', shiftKey: true, handler: () => this.selectUser(true) }, // Shift+Enter
		]

		otherHandlers.forEach((data) => {
			// https://stackoverflow.com/a/63803445/1313904
			// no idea why I have to register these handlers differently,
			// but this is the only way it seems to have worked
			quill.keyboard.bindings[data.key].unshift({ ...data, collapsed: true })
		})

		this.addQuillEvent('text-change', () => this.onTextChange())
		this.addQuillEvent('selection-change', (range) =>
			this.onSelectionChange(range)
		)

		this.addDOMEvent(this.quill.container, 'paste', () => this.onPaste())
		this.addDOMEvent(document.body, 'click', (e) => {
			if (!this.open || this.container.contains(e.target)) return
			this.hideSuggestions()
		})

		this.removeEventBusEvents = addEventBusEvents({
			quillPasteEvent: () => this.onPaste(),
		})
	}

	destroy() {
		this.hideSuggestions()
		this.removeEventBusEvents?.()
		this.quillEvents?.forEach((e) => this.quill.off(e.event, e.handler))
		this.domEvents?.forEach((e) =>
			e.element.removeEventListener(e.event, e.handler)
		)
		// These callbacks were created via `this.method.bind(this)` in Editor.vue
		// and retain the Editor component via their bound `this`. Null them here
		// so the module object (which may still be held by Quill's graph after
		// destroy) doesn't pin the Editor and everything upstream.
		this.getExplicitResults = null
		this.getImplicitResults = null
		this.getSuggestion = null
		this.getUsersInText = null
		this.onSuggestionsChange = null
	}

	addQuillEvent(event, handler) {
		this.quill.on(event, handler)
		this.quillEvents = this.quillEvents || []
		this.quillEvents.push({ event, handler })
	}

	addDOMEvent(element, event, handler) {
		element.addEventListener(event, handler)
		this.domEvents = this.domEvents || []
		this.domEvents.push({ element, event, handler })
	}

	selectUser(auto_insert = false, spacebar = false) {
		if (!this.open) return true
		if (this.isLoading) return spacebar
		const button = this.getSelectedButton()
		const { user: id, fullname, me } = button.dataset

		if (
			(auto_insert &&
				this.query.slice(1).toLowerCase() !== fullname.toLowerCase()) ||
			(spacebar && this.suggestions?.length > 1)
		)
			return true

		if (!id) {
			this.close(null)
			return true
		}
		this.close({ id, fullname, me }, auto_insert)

		if (auto_insert) {
			return true
		}
	}

	moveToNext() {
		if (!this.open) return true
		const current = this.getSelectedButton() || {}
		this.highlight(current.nextSibling || this.firstUser)
	}

	moveToPrev() {
		if (!this.open) return true
		const current = this.getSelectedButton() || {}
		this.highlight(current.previousSibling || this.lastUser)
	}

	onSelectionChange(range) {
		if (range && range.length > 0) {
			// if user selects text, close suggestions
			this.close(null)
			return true
		}
		// otherwise, update suggestions based on
		// the current caret position
		this.onTextChange()
	}

	async onTextChange() {
		await sleep(1) // see PR #1170 for more details on this sleep
		if (this.pasteInProgress) return true
		const selection = this.quill.getSelection()
		if (!selection) return true
		const { index: caret } = selection
		const text = this.quill.getText(0, caret)
		// check for explicit match first
		this.startIndex = lastIndexOfRegex(text, indexREGEX)
		if (this.startIndex > -1) {
			let query = (this.query = text.slice(this.startIndex, text.length))

			if (!query.startsWith('@')) {
				this.startIndex += 1
				query = this.query = query.replace(RegExp(allowedChars), '')
			}

			this.showLoadingState()
			const results = await this.getExplicitResults(query)
			if (results === null) return false
			this.renderSuggestions(results)
			return false
		}
		// check for implicit match after
		this.startIndex = lastIndexOfRegex(text, /\s/g) + 1
		const query = (this.query = text.slice(this.startIndex, text.length))
		if (query.length >= MIN_CHARS) {
			this.showLoadingState()
			const results = this.getImplicitResults(query)
			if (results === null) return false
			this.renderSuggestions(results)
			return false
		}
		this.close(null)
	}

	replaceAllMentions() {
		const contents = this.quill
			.getContents()
			.map((c) =>
				c.insert.mention ? { insert: `@${c.insert.mention.fullname}` } : c
			)
		this.quill.setContents({ ops: contents }, Quill.sources.SILENT)
	}

	async resolveMentions() {
		const text = this.quill.getText()
		// get all matching users in the entire message
		const users = await this.getUsersInText(text)
		// in order for the offset to work properly,
		// we have to go through the list in order of index
		users
			.sort((a, b) => a.index - b.index)
			// go through all users and insert their mention
			.reduce((offset, { user, index, text }) => {
				const adjustedIndex = index - offset
				this.quill.insertEmbed(
					adjustedIndex,
					'mention',
					user,
					Quill.sources.SILENT
				)
				this.quill.deleteText(
					adjustedIndex + 1,
					text.length,
					Quill.sources.SILENT
				)
				return offset + text.length - 1
			}, 0)
		return users
	}

	moveCursorToEnd() {
		this.quill.setSelection(this.quill.getLength() - 1)
	}

	onPaste() {
		this.pasteInProgress = true
		window.requestAnimationFrame(() => {
			// remove any previous mentions in order to preserve the
			// proper index locations of all the old mentions as well
			// as any new ones we might discover
			this.replaceAllMentions()
			setTimeout(() => {
				this.moveCursorToEnd()
				// quickly move cursor to the end of the text
				this.moveCursorToEnd()
				// now start resolving mentions in the resulting text
				this.resolveMentions().then(() => {
					this.pasteInProgress = false
					setTimeout(() => this.moveCursorToEnd())
				})
			})
		})
	}

	showLoadingState() {
		if (!this.suggestions.length) return false
		this.loadingDiv.style.display = 'flex'
		this.isLoading = true
	}

	hideLoadingState() {
		this.loadingDiv.style.display = 'none'
		this.isLoading = false
	}

	renderSuggestions(users) {
		this.clear()
		this.hideLoadingState()

		if (!users || !users.length) {
			return this.hideSuggestions()
		}

		const suggestions = users.map((user) => {
			const item = this.getSuggestion(user)
			this.container.appendChild(item)
			this.addSuggestionEvent(item, 'click', () => this.close(user))
			this.addSuggestionEvent(item, 'mouseover', () => this.highlight(item))
			return item
		})

		this.firstUser = suggestions[0]
		this.lastUser = suggestions.slice(-1)[0]
		this.suggestions = suggestions

		this.container.style.display = 'block'
		this.highlight(this.firstUser)
		this.open = true
		this.onSuggestionsChange(true, 'mention')
		if (!this.escapeId) {
			this.escapeId = addEscapeListener(() => {
				this.hideSuggestions()
			})
		}
	}

	addSuggestionEvent(item, event, handler) {
		item.addEventListener(event, handler)
		this.suggestionEvents = this.suggestionEvents || []
		this.suggestionEvents.push({ item, event, handler })
	}

	getSelectedButton() {
		return this.container.querySelector(`.${ACTIVE_CLASS}`)
	}

	highlight(button) {
		const old = this.getSelectedButton()
		if (old) {
			old.classList.remove(ACTIVE_CLASS)
		}
		button.classList.add(ACTIVE_CLASS)
		scrollIntoView(button, {
			scrollMode: 'if-needed',
			block: 'nearest',
			inline: 'nearest',
		})
	}

	clear() {
		this.container.querySelectorAll('.mention-option').forEach((item) => {
			this.container.removeChild(item)
		})
	}

	hideSuggestions() {
		this.suggestionEvents?.forEach((e) =>
			e.item.removeEventListener(e.event, e.handler)
		)
		this.hideLoadingState()
		this.suggestions = []
		this.suggestionEvents = []
		this.container.style.display = 'none'
		this.open = false
		this.onSuggestionsChange(false, 'mention')
		removeEscapeListener(this.escapeId)
		this.escapeId = null
	}

	close(user, nospace = false) {
		this.clear()
		if (user) {
			const sel = this.quill.getSelection()
			if (!sel) {
				this.hideSuggestions()
				return
			}
			const queryLength = this.query.length
			const index = sel.index - queryLength
			this.quill.deleteText(index, queryLength, Quill.sources.USER)
			this.quill.insertEmbed(index, 'mention', user, Quill.sources.USER)
			if (!nospace) {
				this.quill.insertText(index + 1, ' ', Quill.sources.USER)
			}
			this.quill.setSelection(index + 2, 0, Quill.sources.USER)
		}
		this.quill.focus()
		this.hideSuggestions()
	}
}

export default Mentions
